import React from 'react';
import { Provider } from 'react-redux';
import { createStore, applyMiddleware, compose } from 'redux';
import { createLogger } from 'redux-logger';
import thunk from 'redux-thunk';
import { assert, assertIsOneOfTypes } from 'common/assertions';
import { migrateVif } from 'common/visualizations/helpers/migrateVif';

import reducer from './reducers/index';
import getVifTemplates from './vifs';
import { setFilters, setShowLegend, setColorPaletteProperties } from './actions';
import { load } from './vifs/loader';
import { defaultState as defaultMetadata } from './reducers/metadataCollection';
import AuthoringWorkflow from './components/AuthoringWorkflow';

import { Vif } from 'common/visualizations/vif';
import { Filters } from 'common/components/FilterBar/types';
import { get, merge } from 'lodash';

interface AuthoringWorkflowProps {
  vif: Partial<Vif>;
  filters: Filters;
  useLogger?: boolean;
  enableFiltering: boolean;
  backButtonText: Text;
  onBack: () => void;
  onComplete: () => void;
  onCancel: () => void;
}

class AuthoringWorkflowIndex extends React.Component<AuthoringWorkflowProps> {
  store: ReturnType<typeof createStore>;

  constructor(props: AuthoringWorkflowProps) {
    super(props);
    const logger = createLogger();

    let { vif } = this.props;
    const { filters } = this.props;

    const formatVersion = get(vif, 'format.version');
    const formatType = get(vif, 'format.type');

    assertIsOneOfTypes(formatVersion, 'number');
    assert(
      formatType === 'visualization_interchange_format',
      'formatType must be "visualization_interchange_format"',
    );

    vif = this.props.vif ? migrateVif(this.props.vif) : {};
    const vifType = get(vif, 'series[0].type', null);
    // Adding initial VIF to vifs list
    // Serves as a fallback for reset options
    // Pass vif to agTable, since it requires a reference to either an existing or a new table.
    const vifs = merge(getVifTemplates(vifType === 'agTable' ? vif : undefined), { initialVif: vif });
    const initialState = {
      metadataCollection: defaultMetadata,
      vifAuthoring: {
        vifs: vifs,
        authoring: {
          filters,
          selectedVisualizationType: vifType,
          showCenteringAndZoomingSaveMessage: false,
        },
      },
    };

    const middleware: any[] = [thunk];
    if (this.props.useLogger) {
      middleware.push(logger);
    }

    const composeEnhancers =
      (window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ &&
        window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({
          // Uncomment the following line to get tracing in Redux Dev Tools
          //trace: true,
          name: 'AuthoringWorkflow',
        })) ||
      compose;

    this.store = createStore(reducer, initialState, composeEnhancers(applyMiddleware(...middleware)));

    load(this.store.dispatch, vif);
    this.store.dispatch(setFilters(filters));
    this.store.dispatch(setShowLegend(get(vif, 'series[0].showLegend', true)));
    /**
     * TODO: EN-77584
     * `setColorPaletteProperties` returns `(dispatch: any, getState: any) => void`, but
     * `dispatch` wants `Action<any>`. It's unclear how to make the type of `setColorPaletteProperties`
     * right, so we'll work around this by casting to `any`.
     */
    this.store.dispatch(setColorPaletteProperties() as any);
  }

  render() {
    return (
      <Provider store={this.store}>
        <AuthoringWorkflow {...this.props} />
      </Provider>
    );
  }
}

export default AuthoringWorkflowIndex;
